import { Button } from '@components/ui';
import Modal from '@components/ui/Modal/Modal';
import { useTranslation } from '@/hooks/useTranslation';
import { useAuthStore } from '@/stores/authStore';
import { toTitleCase } from '@utils/textCase';
import type { PlatformUserDto } from '@/types/user';
import styles from './UserModals.module.scss';

// ============================================
// TYPES
// ============================================

interface ViewUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: PlatformUserDto | null;
  onEdit: () => void;
  onAudit?: () => void;
}

// ============================================
// COMPONENT
// ============================================

function ViewUserModal({ isOpen, onClose, data, onEdit, onAudit }: ViewUserModalProps) {
  const { t } = useTranslation();
  const hasPermission = useAuthStore((s) => s.hasPermission);
  if (!data) return null;

  const statusClass =
    data.status === 'ACTIVE' ? styles.statusBadgeACTIVE : styles.statusBadgeINACTIVE;

  const typeClass =
    data.userType === 'ADMIN'
      ? styles.typeBadgeADMIN
      : data.userType === 'EMPLOYEE'
        ? styles.typeBadgeEMPLOYEE
        : styles.typeBadgeSTANDARD;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`${data.firstName} ${data.lastName}`.trim()}
      size="md"
      footer={
        <div className={styles.viewFooter}>
          <Button variant="outline" size="sm" onClick={onClose}>
            {t('K_CLOSE', 'Close')}
          </Button>
          {onAudit && hasPermission('AUDIT_READ') && (
            <Button variant="outline" size="sm" onClick={onAudit}>
              {t('K_AUDIT_TITLE', 'Audit trail')}
            </Button>
          )}
          {hasPermission('USERS_UPDATE') && (
            <Button variant="filled" size="sm" onClick={onEdit}>
              {t('K_EDIT', 'Edit')}
            </Button>
          )}
        </div>
      }
    >
      <div className={styles.viewBody}>
        <div className={styles.fieldGrid}>
          <div className={styles.fieldItem}>
            <span className={styles.fieldLabel}>{t('K_LABEL_FIRST_NAME', 'First Name')}</span>
            <span className={styles.fieldValue}>{data.firstName}</span>
          </div>
          <div className={styles.fieldItem}>
            <span className={styles.fieldLabel}>{t('K_LABEL_LAST_NAME', 'Last Name')}</span>
            <span className={styles.fieldValue}>{data.lastName || '-'}</span>
          </div>
          <div className={styles.fieldItem}>
            <span className={styles.fieldLabel}>{t('K_LABEL_EMAIL', 'Email')}</span>
            <span className={styles.fieldValue}>{data.email}</span>
          </div>
          <div className={styles.fieldItem}>
            <span className={styles.fieldLabel}>{t('K_LABEL_MOBILE', 'Mobile')}</span>
            <span className={styles.fieldValue}>{data.mobile || '-'}</span>
          </div>
          <div className={styles.fieldItem}>
            <span className={styles.fieldLabel}>{t('K_LABEL_USER_TYPE', 'User Type')}</span>
            <span className={`${styles.typeBadge} ${typeClass}`}>
              {toTitleCase(data.userType)}
            </span>
          </div>
          <div className={styles.fieldItem}>
            <span className={styles.fieldLabel}>{t('K_LABEL_STATUS', 'Status')}</span>
            <span className={`${styles.statusBadge} ${statusClass}`}>
              {toTitleCase(data.status)}
            </span>
          </div>
          <div className={styles.fieldItem}>
            <span className={styles.fieldLabel}>{t('K_LABEL_LOCALE', 'Locale')}</span>
            <span className={styles.fieldValue}>{data.locale || '-'}</span>
          </div>
          <div className={styles.fieldItem}>
            <span className={styles.fieldLabel}>{t('K_LABEL_TIMEZONE', 'Timezone')}</span>
            <span className={styles.fieldValue}>{data.timezone || '-'}</span>
          </div>
          <div className={styles.fieldItem}>
            <span className={styles.fieldLabel}>{t('K_LABEL_EFFECTIVE_FROM', 'Effective From')}</span>
            <span className={styles.fieldValue}>
              {data.effectiveFrom
                ? new Date(data.effectiveFrom).toLocaleDateString()
                : '-'}
            </span>
          </div>
        </div>

        {/* Roles Section */}
        <div className={styles.rolesSection}>
          <span className={styles.sectionTitle}>{t('K_LABEL_ROLES', 'Roles')}</span>
          {data.roles.length > 0 ? (
            <div className={styles.roleBadges}>
              {data.roles.map((role) => (
                <span key={role.id} className={styles.roleBadge}>
                  {role.name}
                </span>
              ))}
            </div>
          ) : (
            <span className={styles.noRoles}>
              {t('K_EMPTY_NO_ROLES_ASSIGNED', 'No roles assigned')}
            </span>
          )}
        </div>
      </div>
    </Modal>
  );
}

export default ViewUserModal;
